import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Siren, BellRinging, BellSlash, SpeakerHigh, Pulse, Waveform, TestTube, CaretDown
} from '@phosphor-icons/react';

const LS_KEY = 'traffic_alerts_config_v1';

const loadConfig = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* ignore */ }
  return { enabled: false, monitored: [], threshold: 10, lowGapMin: 5, pollSec: 20 };
};

// ---- Audio helpers (loud attention siren + spoken message) ----
let audioCtx = null;
const getCtx = () => {
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) audioCtx = new AC();
  }
  if (audioCtx && audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
};

const playSiren = () => {
  const ctx = getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;
  // three rising "whoop" tones
  for (let i = 0; i < 3; i++) {
    const t0 = now + i * 0.45;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(660, t0);
    osc.frequency.exponentialRampToValueAtTime(1320, t0 + 0.35);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.55, t0 + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.42);
  }
};

const speak = (message) => {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(message);
    u.volume = 1; u.rate = 1; u.pitch = 1.05;
    // slight delay so the siren doesn't clash with the first word
    setTimeout(() => synth.speak(u), 900);
  } catch (e) { /* ignore */ }
};

const fireAlert = (message) => {
  playSiren();
  speak(message);
};

const StateBadge = ({ state }) => {
  const map = {
    active: { c: '#39FF14', t: 'LIVE' },
    paused: { c: '#f59e0b', t: 'IDLE' },
    idle: { c: '#64748b', t: 'QUIET' },
  };
  const s = map[state] || map.idle;
  return (
    <span className="text-[10px] font-mono font-bold px-2 py-0.5 border" style={{ color: s.c, borderColor: `${s.c}55`, background: `${s.c}12` }}>
      {s.t}
    </span>
  );
};

const TrafficAlerts = ({ sites }) => {
  const [cfg, setCfg] = useState(loadConfig);
  const [open, setOpen] = useState(true);
  const [statuses, setStatuses] = useState({}); // siteId -> { cpm, state, active }
  const [log, setLog] = useState([]);
  const machineRef = useRef({}); // siteId -> { primed, everActive, isActive, belowSince }
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;

  // persist config
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(cfg)); }, [cfg]);

  const siteName = useCallback((id) => {
    const s = sites.find(x => x.id === id);
    return s ? s.name : id;
  }, [sites]);

  const pushLog = useCallback((siteId, kind, cpm) => {
    setLog(prev => [{ id: `${Date.now()}-${siteId}`, siteId, name: siteName(siteId), kind, cpm, ts: Date.now() }, ...prev].slice(0, 12));
  }, [siteName]);

  const evaluate = useCallback((siteId, cpm) => {
    const c = cfgRef.current;
    const threshold = c.threshold;
    const lowGapMs = c.lowGapMin * 60 * 1000;
    const now = Date.now();
    const m = machineRef.current[siteId] || { primed: false, everActive: false, isActive: false, belowSince: null };

    const above = cpm >= threshold;

    if (!m.primed) {
      // establish baseline silently on first reading (avoids alert on page open)
      m.primed = true;
      m.isActive = above;
      m.everActive = above;
      m.belowSince = above ? null : now;
      machineRef.current[siteId] = m;
      return above ? 'active' : 'idle';
    }

    if (above) {
      if (!m.isActive) {
        // rising edge
        if (!m.everActive) {
          fireAlert(`Traffic has started, alert! ${siteName(siteId)}`);
          pushLog(siteId, 'started', cpm);
          toast.success(`🚨 Traffic started — ${siteName(siteId)} (${cpm}/min)`);
        } else if (m.belowSince && (now - m.belowSince) >= lowGapMs) {
          fireAlert(`Traffic restarted! ${siteName(siteId)}`);
          pushLog(siteId, 'restarted', cpm);
          toast.success(`🔁 Traffic restarted — ${siteName(siteId)} (${cpm}/min)`);
        }
        m.isActive = true;
        m.everActive = true;
        m.belowSince = null;
      }
      machineRef.current[siteId] = m;
      return 'active';
    } else {
      if (m.isActive) {
        m.isActive = false;
        m.belowSince = now;
      } else if (m.belowSince === null && m.everActive) {
        m.belowSince = now;
      }
      machineRef.current[siteId] = m;
      // paused = was active and has been low long enough
      const paused = m.everActive && m.belowSince && (now - m.belowSince) >= lowGapMs;
      return paused ? 'paused' : (m.everActive ? 'idle' : 'idle');
    }
  }, [siteName, pushLog]);

  // polling loop
  useEffect(() => {
    if (!cfg.enabled || cfg.monitored.length === 0) {
      setStatuses({});
      return;
    }
    let cancelled = false;

    const tick = async () => {
      const ids = cfgRef.current.monitored;
      await Promise.all(ids.map(async (id) => {
        try {
          const res = await axios.get(`/api/analytics/pulse?website_id=${id}&window=60`, { withCredentials: true });
          if (cancelled) return;
          const cpm = res.data.clicks_per_min || 0;
          const state = evaluate(id, cpm);
          setStatuses(prev => ({ ...prev, [id]: { cpm, state, active: res.data.active || 0 } }));
        } catch (e) { /* ignore transient errors */ }
      }));
    };

    tick();
    const iv = setInterval(tick, Math.max(15, cfg.pollSec) * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [cfg.enabled, cfg.monitored, cfg.pollSec, evaluate]);

  const toggleMonitored = (id) => {
    setCfg(c => {
      const has = c.monitored.includes(id);
      // reset that site's machine when toggling
      delete machineRef.current[id];
      return { ...c, monitored: has ? c.monitored.filter(x => x !== id) : [...c.monitored, id] };
    });
  };

  const setEnabled = (v) => {
    if (v) getCtx(); // unlock audio on the enabling click (user gesture)
    machineRef.current = {}; // fresh baseline
    setCfg(c => ({ ...c, enabled: v }));
  };

  const testAlert = () => {
    getCtx();
    fireAlert('Traffic has started, alert! Test profile');
    toast('🔊 Test alert fired');
  };

  return (
    <div className="bg-card border border-white/10 mb-6" data-testid="traffic-alerts-section">
      {/* Header */}
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="alerts-toggle-open"
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-3">
          {cfg.enabled
            ? <Siren size={20} className="text-[#39FF14]" weight="fill" />
            : <BellSlash size={20} className="text-slate-500" weight="bold" />}
          <div className="text-left">
            <h3 className="text-sm uppercase tracking-widest font-bold text-slate-100">Traffic Alerts</h3>
            <p className="text-[11px] font-mono text-slate-500">
              {cfg.enabled
                ? `Monitoring ${cfg.monitored.length} profile${cfg.monitored.length === 1 ? '' : 's'} · ≥${cfg.threshold}/min`
                : 'Audio alerts are off'}
            </p>
          </div>
        </div>
        <CaretDown size={16} className={`text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-white/5 pt-4 space-y-5 animate-in fade-in duration-300">
          {/* Master controls */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setEnabled(!cfg.enabled)}
              data-testid="alerts-enable-btn"
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${cfg.enabled ? 'bg-[#39FF14] text-black' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
            >
              {cfg.enabled ? <BellRinging size={15} weight="fill" /> : <BellSlash size={15} weight="bold" />}
              {cfg.enabled ? 'Alerts On' : 'Alerts Off'}
            </button>
            <button
              onClick={testAlert}
              data-testid="alerts-test-btn"
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest bg-white/5 text-slate-300 hover:bg-white/10 transition-colors"
            >
              <TestTube size={15} weight="bold" /> Test Sound
            </button>

            <div className="flex items-center gap-2 ml-auto">
              <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
                <Pulse size={13} weight="bold" /> Threshold
              </label>
              <input
                type="number" min={1} max={1000}
                data-testid="alerts-threshold-input"
                value={cfg.threshold}
                onChange={e => setCfg(c => ({ ...c, threshold: Math.max(1, parseInt(e.target.value) || 1) }))}
                className="w-16 bg-black/40 border border-white/15 px-2 py-1.5 text-white text-sm font-mono focus:outline-none focus:border-primary"
              />
              <span className="text-[11px] text-slate-500 font-mono">clicks/min</span>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">Restart gap</label>
              <input
                type="number" min={1} max={120}
                data-testid="alerts-lowgap-input"
                value={cfg.lowGapMin}
                onChange={e => setCfg(c => ({ ...c, lowGapMin: Math.max(1, parseInt(e.target.value) || 1) }))}
                className="w-14 bg-black/40 border border-white/15 px-2 py-1.5 text-white text-sm font-mono focus:outline-none focus:border-primary"
              />
              <span className="text-[11px] text-slate-500 font-mono">min low</span>
            </div>
          </div>

          {/* Profile selection */}
          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center gap-1">
              <SpeakerHigh size={13} weight="bold" /> Alert me from these profiles
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="alerts-profile-list">
              {sites.map(s => {
                const on = cfg.monitored.includes(s.id);
                const status = statuses[s.id];
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleMonitored(s.id)}
                    data-testid={`alerts-profile-${s.id}`}
                    className={`flex items-center justify-between gap-2 px-3 py-2.5 border text-left transition-colors ${on ? 'border-primary/60 bg-primary/10' : 'border-white/10 bg-black/20 hover:border-white/25'}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`w-3.5 h-3.5 border flex items-center justify-center text-[9px] ${on ? 'bg-primary border-primary text-black' : 'border-white/30'}`}>
                          {on ? '✓' : ''}
                        </span>
                        <span className="text-sm text-white truncate">{s.name}</span>
                      </div>
                      <span className="text-[10px] font-mono text-slate-500 truncate block ml-5.5">{s.domain}</span>
                    </div>
                    {on && cfg.enabled && (
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <StateBadge state={status?.state || 'idle'} />
                        <span className="text-[11px] font-mono text-slate-300">
                          <Waveform size={11} className="inline mr-0.5 text-primary" weight="bold" />
                          {status ? `${status.cpm}/min` : '…'}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recent alert log */}
          {log.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-2">Recent alerts</p>
              <div className="space-y-1" data-testid="alerts-log">
                {log.map(l => (
                  <div key={l.id} className="flex items-center justify-between px-3 py-1.5 bg-black/20 border border-white/5 text-xs">
                    <span className="flex items-center gap-2">
                      <Siren size={13} className={l.kind === 'restarted' ? 'text-amber-400' : 'text-[#39FF14]'} weight="fill" />
                      <span className="text-slate-200 font-bold">{l.kind === 'restarted' ? 'Traffic restarted' : 'Traffic started'}</span>
                      <span className="text-slate-500 font-mono">· {l.name}</span>
                    </span>
                    <span className="text-slate-500 font-mono">{l.cpm}/min · {new Date(l.ts).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!cfg.enabled && (
            <p className="text-[11px] font-mono text-slate-600">
              Turn alerts on and pick at least one profile. Your browser will play a loud siren and speak the alert when clicks/min crosses the threshold. Keep this tab open.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TrafficAlerts;
